import { useMemo, useState } from "react";
import { Plus } from "lucide-react";
import {
  Button,
  EmptyState,
  MeasurementTrendChart,
  SegmentedControl,
  Skeleton,
  cn,
  type MeasurementPoint,
} from "@playbookai/ui";
import type { KpiPerformanceStatus, ManagedKpiDetail } from "../api/types.js";
import {
  KPI_OBSERVATION_RANGE_LABEL,
  KPI_OBSERVATION_RANGES,
  useKpiObservations,
  type ManagedKpiObservationRange,
} from "../hooks/useKpiObservations.js";
import { AddHistoricalValuesModal } from "./AddHistoricalValuesModal.js";
import { parseObservationValue } from "./kpi-observation-value.js";
import {
  REPORTING_FREQUENCY_LABEL,
  formatDate,
  formatDateTime,
} from "./kpi-presentation.js";
import {
  nextReportingDueAt,
  reportingPeriodLabel,
} from "./kpi-reporting-period.js";
import { KpiPanel } from "./KpiPanel.js";
import { KpiStatusBadges } from "./KpiStatusBadges.js";
import { KpiValueHistoryTable } from "./KpiValueHistoryTable.js";
import { RecordKpiValueModal } from "./RecordKpiValueModal.js";

export interface KpiPerformanceSectionProps {
  readonly kpi: ManagedKpiDetail;
  /** Whether to offer the two write affordances. PRESENTATION ONLY — the page
   * derives it from the signed-in identity, and `POST /v1/kpis/:id/observations`
   * re-checks the actor against `ownerUserId` server-side. */
  readonly canRecord: boolean;
  /** Tenant-admin — only words the empty state ("assign an owner"). */
  readonly canManage: boolean;
  /** The chosen history window — PAGE state, held by the parent. */
  readonly range: ManagedKpiObservationRange;
  readonly onRangeChange: (range: ManagedKpiObservationRange) => void;
}

/** The hero value carries its performance tone (the status pill's colour,
 * on text) so the reading and its verdict are one glance. */
const HERO_VALUE_TEXT: Record<KpiPerformanceStatus, string> = {
  not_available: "text-pb-text",
  green: "text-pb-green-text",
  amber: "text-pb-amber-text",
  red: "text-pb-red-text",
};

// Kept hand-built (not DescriptionList): dd here is text-body, DescriptionItem's
// details is text-xs — a size step no T01 row approves; and the wrapping-row
// layout (several groups in one flex-wrap line) doesn't match any DescriptionList
// layout mode.
function Cadence({
  label,
  children,
}: {
  readonly label: string;
  readonly children: React.ReactNode;
}): JSX.Element {
  return (
    <div>
      <dt className="mb-0.75 text-dense-1 font-semibold uppercase tracking-wider text-pb-text-muted">
        {label}
      </dt>
      <dd className="text-body font-medium text-pb-text">{children}</dd>
    </div>
  );
}

/**
 * Performance (PB-2941, d37) — the reading history behind the current value:
 * the value itself, its status pills, the range control, the chart, the
 * cadence line and the recorded values. Both write affordances are the
 * Owner's and neither is a Tenant Admin's (ADR-0052): an Admin decides what a
 * KPI should reach, the Owner reports what it read. Every write APPENDS to the
 * ledger, so the buttons say "Add", never "Update".
 *
 * THE CADENCE LINE IS DERIVED, NOT STORED: `Current period` and `Next value
 * due` are pure functions of the latest reading's `observedAt` and the KPI's
 * `reportingFrequency` (see `kpi-reporting-period.ts`), so the cadence is
 * rolling and no due date is invented without a reading or a frequency.
 */
export function KpiPerformanceSection({
  kpi,
  canRecord,
  canManage,
  range,
  onRangeChange,
}: KpiPerformanceSectionProps): JSX.Element {
  const [recordOpen, setRecordOpen] = useState(false);
  const [backfillOpen, setBackfillOpen] = useState(false);
  const observations = useKpiObservations(kpi.kpiId, range);

  const points = useMemo<MeasurementPoint[]>(() => {
    const rows = observations.data ?? [];
    return rows.flatMap((row) => {
      const value = parseObservationValue(row.value);
      const t = Date.parse(row.observedAt);
      return value === null || Number.isNaN(t) ? [] : [{ t, value }];
    });
  }, [observations.data]);

  const target = kpi.currentTarget;
  const targetNumber = target
    ? parseObservationValue(target.targetValue)
    : null;
  const targetDate = target?.targetDate
    ? Date.parse(`${target.targetDate}T00:00:00.000Z`)
    : Number.NaN;

  const observedAt = kpi.currentState?.observationTime ?? null;
  const hasAnyReading = observedAt !== null;
  const periodLabel = reportingPeriodLabel(
    observedAt ?? "",
    kpi.reportingFrequency,
  );
  const dueAt = observedAt
    ? nextReportingDueAt(observedAt, kpi.reportingFrequency)
    : null;

  const addValue = (size: "sm" | "md" = "sm") => (
    <Button variant="primary" size={size} onClick={() => setRecordOpen(true)}>
      <Plus className="h-3.5 w-3.5" aria-hidden="true" />
      Add value
    </Button>
  );

  return (
    <KpiPanel
      title="Performance"
      subtitle="The reading history behind the current value."
      headingId="kpi-performance-heading"
      action={
        canRecord ? (
          <>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setBackfillOpen(true)}
            >
              Add historical values
            </Button>
            {/* Before the first reading the empty state carries the one Add
                value, so a region never holds two controls with one name. */}
            {hasAnyReading ? addValue() : null}
          </>
        ) : null
      }
    >
      {!hasAnyReading ? (
        <EmptyState
          variant="first-use"
          title="No values recorded yet"
          description={
            canRecord
              ? "Record the first value to start the history and chart."
              : canManage
                ? "The KPI’s owner records the first value. Assign an owner to get started."
                : "Values appear here once the owner records them."
          }
          {...(canRecord ? { action: addValue() } : {})}
        />
      ) : (
        <>
          <div className="mb-3.5 flex flex-wrap items-end gap-4">
            <div>
              {/* Kept hand-built (not KPIValue): the hero figure is
                  font-mono, and KPIValue's figure is font-semibold with no
                  monospace option — see KpiTargetSection.tsx. */}
              <p className="flex items-baseline gap-1.75">
                <span
                  className={cn(
                    "font-mono text-display leading-none tracking-tight tabular-nums",
                    HERO_VALUE_TEXT[
                      kpi.currentState?.performanceStatus ?? "not_available"
                    ],
                  )}
                >
                  {kpi.currentState?.currentValue ?? "—"}
                </span>
                {kpi.unit ? (
                  <span className="text-heading-3 font-medium text-pb-text-muted">
                    {kpi.unit}
                  </span>
                ) : null}
              </p>
              <p className="mt-1.75 text-caption text-pb-text-muted">
                Last observed {formatDateTime(observedAt)}
              </p>
            </div>
            <KpiStatusBadges
              state={kpi.currentState}
              hint={false}
              performanceFirst
            />
            <div className="ms-auto">
              <SegmentedControl
                size="sm"
                value={range}
                onChange={onRangeChange}
                aria-label="History range"
                options={KPI_OBSERVATION_RANGES.map((value) => ({
                  value,
                  label: KPI_OBSERVATION_RANGE_LABEL[value],
                }))}
              />
            </div>
          </div>

          {observations.isLoading ? (
            <Skeleton
              className="h-56 w-full"
              shape="panel"
              aria-label="Loading history"
            />
          ) : observations.isError ? (
            <EmptyState
              variant="error"
              title="History could not be loaded"
              description="The reading history is temporarily unavailable. Try the request again."
              action={
                <Button onClick={() => void observations.refetch()}>
                  Retry
                </Button>
              }
            />
          ) : (
            <>
              <MeasurementTrendChart
                ariaLabel={`${kpi.name} reading history`}
                points={points}
                {...(kpi.unit ? { unit: kpi.unit } : {})}
                {...(targetNumber !== null ? { target: targetNumber } : {})}
                {...(Number.isNaN(targetDate) ? {} : { targetDate })}
                emptyMessage={
                  canRecord
                    ? "No readings in this range yet. Use Add value to record one."
                    : "No readings in this range yet."
                }
              />
              <dl className="mt-3.75 flex flex-wrap gap-6.5 border-t border-pb-border/50 pt-3.5">
                <Cadence label="Reporting frequency">
                  {kpi.reportingFrequency
                    ? REPORTING_FREQUENCY_LABEL[kpi.reportingFrequency]
                    : "Not set"}
                </Cadence>
                <Cadence label="Current period">
                  {periodLabel ?? (
                    <span className="text-pb-text-muted">
                      No reading to date from
                    </span>
                  )}
                </Cadence>
                <Cadence label="Next value due">
                  {dueAt ? (
                    formatDate(dueAt)
                  ) : (
                    <span className="text-pb-text-muted">
                      {kpi.reportingFrequency === null
                        ? "Not scheduled"
                        : "After the first reading"}
                    </span>
                  )}
                </Cadence>
              </dl>
              <div className="mt-4">
                <KpiValueHistoryTable
                  kpi={kpi}
                  observations={observations.data ?? []}
                />
              </div>
            </>
          )}
        </>
      )}

      <RecordKpiValueModal
        kpiId={kpi.kpiId}
        unit={kpi.unit}
        open={recordOpen}
        onClose={() => setRecordOpen(false)}
      />
      <AddHistoricalValuesModal
        kpiId={kpi.kpiId}
        unit={kpi.unit}
        reportingFrequency={kpi.reportingFrequency}
        open={backfillOpen}
        onClose={() => setBackfillOpen(false)}
      />
    </KpiPanel>
  );
}
